import React, { useEffect, useState } from "react";
import { appConfig } from "../config";
import { authService } from "../services/authService";
import { UserClaims } from "../types";
import { LoadingState } from "./LoadingState";

type RedeemPayload = {
  accessToken?: unknown;
  claims?: unknown;
  returnTo?: unknown;
};

const safeReturnTo = (value: unknown): string => {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/auth/")) {
    return "/studies";
  }
  return value;
};

export const redeemSsoCode = async (
  search: string,
  fetchImpl: typeof fetch = fetch
): Promise<{ returnTo: string; error: string | null }> => {
  const code = new URLSearchParams(search).get("code");
  if (!code) {
    return { returnTo: "/studies", error: "This sign-in link is incomplete. Return to the originating application and try again." };
  }

  try {
    const response = await fetchImpl(`${appConfig.apiBaseUrl}/auth/sso/redeem`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
      cache: "no-store"
    });
    const body = await response.json().catch(() => ({}));
    const payload = (body?.data || {}) as RedeemPayload;
    if (!response.ok || typeof payload.accessToken !== "string" || !payload.claims || typeof payload.claims !== "object") {
      return { returnTo: "/studies", error: "This sign-in link is invalid or has expired. Return to the originating application and try again." };
    }

    authService.setVerifiedSession(payload.accessToken, payload.claims as UserClaims);
    return { returnTo: safeReturnTo(payload.returnTo), error: null };
  } catch {
    return { returnTo: "/studies", error: "Panel Tools could not complete automatic sign-in. Return to the originating application and try again." };
  }
};

export const SsoCallback = () => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const search = window.location.search;
    window.history.replaceState({}, "", "/auth/sso");
    let active = true;
    void redeemSsoCode(search).then((result) => {
      if (!active) return;
      if (result.error) {
        setError(result.error);
        return;
      }
      window.location.replace(result.returnTo);
    });
    return () => { active = false; };
  }, []);

  if (error) {
    return (
      <main className="app-shell unauth">
        <div className="card auth-card" role="alert">
          <h1>Automatic sign-in could not be completed</h1>
          <p>{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="app-shell unauth">
      <LoadingState label="Completing secure sign-in..." layout="page" />
    </main>
  );
};
